import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useState, useEffect, useRef } from "react";
import { Check } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { LastUsedIndicator } from "./components/LastUsedIndicator";
import { SocialProvidersRow } from "./components/SocialProvidersRow";
import { useAuthForm } from "./hooks/useAuthForm";
import { useAuthHandlers } from "./hooks/useAuthHandlers";
import { WaitlistDialog } from "./WaitlistDialog";
import { StorageWarning } from "./StorageWarning";
import { useAuthStore } from "@/stores/authStore";
import { useSearchParams } from "react-router-dom";
import { isStorageSafeEnvironment } from "@/constants/environments";
import { validateEmailWithSuggestion, validateEmailFormat, validateEmailDeep } from "@/lib/emailValidation";
import { getRiskBand } from "@/utils/getRiskBand";
 

export function LoginForm() {
  const { 
    storageBlocked, 
    sessionError, 
    authError, 
    waitlist, 
    lastAuthMethod,
    clearWaitlistDialog, 
    clearAuthError,
    loadAuthMethod 
  } = useAuthStore();
  const [searchParams] = useSearchParams();
  const form = useAuthForm();
  const { 
    isLoading, 
    authMode, 
    setAuthMode, 
    onSubmit, 
    handleGoogleSignIn, 
    handleMicrosoftSignIn,
    handleLinkedInSignIn
  } = useAuthHandlers();
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const honeypotRef = useRef<HTMLInputElement | null>(null);
  const showApplePlaceholder = import.meta.env.VITE_AUTH_APPLE_COMING_SOON !== 'false';
  const deepValidationEnabled = import.meta.env.VITE_EMAIL_DEEP_VALIDATION !== 'true';
  const signupNameFieldsEnabled = import.meta.env.VITE_SIGNUP_NAME_FIELDS === 'true';

  useEffect(() => {
    console.log(`[Auth] LoginForm mounted, authMode:`, authMode);
    // Load auth method when component mounts
    loadAuthMethod();
  }, [authMode, loadAuthMethod]);

  // Smooth reveal for left-panel checklist
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setReveal(true), 50);
    return () => window.clearTimeout(t);
  }, []);

  // Prefill email from URL param if provided (e.g., from RiskIQ gate)
  useEffect(() => {
    const prefill = (searchParams.get('email') || '').trim();
    if (prefill) {
      form.setValue('email', prefill, { shouldValidate: false, shouldDirty: false });
    }
  }, [searchParams, form]);

  // Initialize mode from URL (?mode=signup|signin|forgot)
  useEffect(() => {
    const mode = (searchParams.get('mode') || '').toLowerCase();
    if (mode === 'signup' && authMode !== 'signup') setAuthMode('signup');
    if (mode === 'forgot' && authMode !== 'forgot') setAuthMode('forgot');
    if (mode === 'signin' && authMode !== 'signin') setAuthMode('signin');
  }, [searchParams, setAuthMode]);

  const getTitle = () => {
    switch (authMode) {
      case 'signup': return "Create your account";
      case 'signin': return "Account Sign in";
      case 'forgot': return "Reset Password";
      default: return "Welcome to AltsIQ";
    }
  };

  const source = searchParams.get('source');
  const scoreParam = searchParams.get('score');
  const bandParam = searchParams.get('band');
  const scoreNum = scoreParam ? Number(scoreParam) : undefined;
  const riskInfo = typeof scoreNum === 'number' && !Number.isNaN(scoreNum) ? getRiskBand(scoreNum) : undefined;

  const leftPanelContent = (
    <div className="text-white px-2 lg:-ml-12 xl:-ml-16 lg:pr-8 xl:pr-12 py-10 md:py-12 lg:py-14 overflow-visible">
      <div className="space-y-[6px]">
        <div className="text-[15px] tracking-[0.22em] uppercase text-white/80">Experience</div>
        <div>
          <span className="font-semibold leading-[1.14] text-[38px] md:text-[42px] lg:text-[40px] xl:text-[48px] whitespace-nowrap" style={{
            backgroundImage: "linear-gradient(90deg, #079FFF 0%, #A569FF 18%, #FF2D55 50%, #FF7F00 75%, #FFED2E 95%, #FFED2E 100%)",
            backgroundSize: "100% 100%",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent"
          }}>Wealth Intelligence</span>
          <span className="ml-1 font-normal leading-none text-[1.35em] md:text-[1.55em] lg:text-[1.65em] align-super relative -top-[0.10em] md:-top-[0.16em] lg:-top-[0.18em]" style={{ color: "#FF7F00", verticalAlign: "super" }}>™</span>
        </div>
        <div className="text-[18px] md:text-[19px] text-white/85">for Everyday Investors</div>
      </div>

      <div className="mt-16">
        <h2 className="font-bold text-[30px] md:text-[32px] leading-tight" style={{ letterSpacing: "-0.02em" }}>
          <span>Every asset. One plan.</span><br />
          <span>Total clarity.</span>
        </h2>
        <ul className="mt-5 space-y-3 text-[16px] md:text-[17px] pl-2 sm:pl-3">
          <li className={`flex items-start gap-3 transition-all duration-500 ease-out ${reveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} delay-100`}>
            <Check className="h-4 w-4 mt-0.5" />
            <span>
              Your RiskIQ Score
              {scoreNum != null && !Number.isNaN(scoreNum) ? (
                <span className="text-[#FFDF20] font-semibold">{' '}– {scoreNum} ({bandParam || riskInfo?.label})</span>
              ) : (
                <><span className="text-[#FFDF20]"> {' '}– </span><a href="/tools/investor-risk-profile" className="underline text-[#FFDF20]" target="_blank" rel="noopener noreferrer">Take assessment</a></>
              )}
            </span>
          </li>
          <li className={`flex items-start gap-3 transition-all duration-500 ease-out ${reveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} delay-200`}><Check className="h-4 w-4 mt-0.5" /> 150+ Canadian brokerages</li>
          <li className={`flex items-start gap-3 transition-all duration-500 ease-out ${reveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} delay-300`}><Check className="h-4 w-4 mt-0.5" /> Misalignment detection</li>
          <li className={`flex items-start gap-3 transition-all duration-500 ease-out ${reveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} delay-500`}><Check className="h-4 w-4 mt-0.5" /> Independent. Zero conflicts.</li>
        </ul>
      </div>

      <div className="mt-12 pt-6">
        <p className="text-xs text-white/80 mb-3">World-class market data</p>
        <div className="flex items-center gap-8 opacity-95">
          <div className="h-6 flex items-center">
            <img src="/logos/partners/bloomberg-white.svg" alt="Bloomberg" className="h-6 w-auto object-contain translate-y-[5px]" loading="lazy" />
          </div>
          <div className="h-6 flex items-center">
            <img src="/logos/partners/fundserv.svg" alt="Fundserv" className="h-6 w-auto object-contain invert" loading="lazy" />
          </div>
          <div className="h-6 flex items-center">
            <img src="/logos/partners/morningstar-white.svg" alt="Morningstar" className="h-6 w-auto object-contain" loading="lazy" />
          </div>
        </div>
      </div>
    </div>
  );

  const getDescription = () => {
    // Show error message if present in URL params
    const error = searchParams.get('error');
    if (error) {
      switch (error) {
        case 'timeout':
          return "Session timed out. Please sign in again.";
        case 'profile_timeout':
          return "Profile setup timed out. Please try signing in again.";
        case 'storage_blocked':
          return "Browser storage is blocked. Please check your browser settings.";
        default:
          // Silent handling for expired tokens - no scary error message
          return null; // Don't show error for normal session expiry
      }
    }

    switch (authMode) {
      case 'signup': return null; // no subline for signup
      case 'signin': return "Welcome back, let’s dive in!";
      case 'forgot': return "Enter your email to receive reset instructions";
      default: return "Welcome to Unis Lumen, let’s get started!";
    }
  };

  return (
    <div
      className={`min-h-screen w-full flex items-center justify-center py-4 px-6 md:px-10 lg:px-16 xl:px-28 ${authMode !== 'signup' ? 'bg-slate-50' : ''} ${authMode === 'signup' ? 'pb-20 lg:pb-8' : ''}`}
      style={authMode === 'signup' ? {
        backgroundImage: "radial-gradient(circle at 12% 18%, rgba(0,69,208,0.16) 0%, rgba(0,69,208,0.08) 28%, rgba(0,69,208,0.04) 50%, transparent 70%), linear-gradient(135deg, #0045D0 0%, #002060 100%)"
      } : undefined}
    >
      <div className={`w-full max-w-7xl grid grid-cols-1 ${authMode === 'signup' ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,500px)] xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)]' : 'lg:grid-cols-12'} gap-8 md:gap-10 lg:gap-12 xl:gap-16 lg:gap-x-12 xl:gap-x-32 md:justify-items-start items-center lg:items-center lg:justify-items-start`}>
        {authMode === 'signup' && (
          <div className="lg:hidden flex justify-start pl-2 pt-2">
            <div className={`text-2xl font-semibold text-white tracking-tight`} style={{ fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif' }}>
              <span>Unis Lumen</span>
              <span style={{ color: "#FF9500" }}>.</span>
            </div>
          </div>
        )}
        {authMode === 'signup' && (
          <div className="lg:hidden">
            <div className="text-white px-2 pt-8 pb-4">
              <div className="space-y-[6px]">
                <div className="text-[14px] tracking-[0.22em] uppercase text-white/80">Experience</div>
                <div>
                  <span className="font-semibold leading-[1.14] text-[36px] whitespace-nowrap" style={{
                    backgroundImage: "linear-gradient(90deg, #079FFF 0%, #A569FF 25%, #FF2D55 53%, #FF7F00 80%, #FFED2E 98%, #FFED2E 100%)",
                    backgroundSize: "100% 100%",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent"
                  }}>Wealth Intelligence</span>
                  <span className="ml-1 font-normal leading-none text-[1.35em] md:text-[1.55em] lg:text-[1.65em] align-super relative -top-[0.10em] md:-top-[0.16em] lg:-top-[0.18em]" style={{ color: "#FF7F00", verticalAlign: "super" }}>™</span>
                </div>
                <div className="text-[16px] text-white/85">for Everyday Investors</div>
              </div>

              <div className="mt-8">
                <h2 className="font-bold text-[26px] leading-tight" style={{ letterSpacing: "-0.02em" }}>
                  <span>Every asset. One plan.</span><br />
                  <span>Total clarity.</span>
                </h2>
                <ul className="mt-4 space-y-2 text-[15px] pl-2">
                  <li className={`flex items-start gap-3 ${reveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transition-all duration-500 ease-out delay-100`}>
                    <Check className="h-4 w-4 mt-0.5" />
                    <span>
                      Your RiskIQ Score
                      {scoreNum != null && !Number.isNaN(scoreNum) ? (
                        <span className="text-[#FFDF20] font-semibold">{' '}– {scoreNum} ({bandParam || riskInfo?.label})</span>
                      ) : (
                        <><span className="text-[#FFDF20]"> {' '}– </span><a href="/tools/investor-risk-profile" className="underline text-[#FFDF20]" target="_blank" rel="noopener noreferrer">Take assessment</a></>
                      )}
                    </span>
                  </li>
                  <li className={`flex items-start gap-3 ${reveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transition-all duration-500 ease-out delay-200`}><Check className="h-4 w-4 mt-0.5" /> 150+ Canadian brokerages</li>
                  <li className={`flex items-start gap-3 ${reveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transition-all duration-500 ease-out delay-300`}><Check className="h-4 w-4 mt-0.5" /> Misalignment detection</li>
                  <li className={`flex items-start gap-3 ${reveal ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'} transition-all duration-500 ease-out delay-500`}><Check className="h-4 w-4 mt-0.5" /> Independent. Zero conflicts.</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        {authMode === 'signup' && (
          <div className="hidden lg:block">
            {leftPanelContent}
          </div>
        )}
        <div className={`w-full ${authMode !== 'signup' ? 'lg:col-start-4 lg:col-span-6' : ''} lg:pl-8 xl:pl-12`}>
          {authMode === 'signup' && (
            <div className="mb-4 hidden lg:flex justify-center">
              <div className={`text-2xl font-semibold text-white tracking-tight`} style={{ fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif' }}>
                <span>Unis Lumen</span>
                <span style={{ color: "#FF9500" }}>.</span>
              </div>
            </div>
          )}
          {authMode === 'signin' && (
            <div className="mb-4 flex justify-center">
              <div className={`text-2xl font-bold tracking-tight`} style={{ fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif' }}>
                <span>Unis Lumen</span>
                <span style={{ color: "#FF9500" }}>.</span>
              </div>
            </div>
          )}
          <Card className={`w-full mx-auto justify-self-center ${authMode === 'signup' ? 'md:mx-0 md:justify-self-start lg:mx-auto lg:justify-self-center md:w-[min(28rem,90vw)] lg:w-auto lg:max-w-[500px]' : ''} ${authMode === 'signin' ? 'max-w-md lg:max-w-[480px]' : 'max-w-md lg:max-w-none'}`}>
          <CardHeader className="space-y-2 text-center">
            <CardTitle className={`font-bold text-xl`}>{getTitle()}</CardTitle>
          {getDescription() && (
            <CardDescription className="text-md">{getDescription()}</CardDescription>
          )}
          </CardHeader>
          <CardContent className="space-y-4">
          {(storageBlocked || sessionError) && (
            isStorageSafeEnvironment() ? null : <StorageWarning />
          )}
          
          {/* Auth Error (OAuth errors, etc.) */}
          {authError && (
            <div className="p-4 border border-destructive rounded-lg bg-destructive/10 flex justify-between items-start">
              <p className="text-destructive text-sm">{authError}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAuthError}
                className="ml-2 h-auto p-1 text-destructive hover:text-destructive/80"
              >
                ✕
              </Button>
            </div>
          )}
          <div className="mt-1" />
          {(authMode !== 'forgot' && ["google","azure","linkedin_oidc","apple"].includes(lastAuthMethod || "")) && (
            <div className="mt-1 mb-2 flex items-center justify-center text-xs text-muted-foreground leading-none">
              <span className="inline-flex items-center gap-1.5 align-middle">
                <span className="italic">You previously signed in with</span>
                {lastAuthMethod === 'google' && (
                  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                )}
                {lastAuthMethod === 'azure' && (
                  <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden="true"><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
                )}
                {lastAuthMethod === 'linkedin_oidc' && (
                  <img
                    src="/logos/partners/icons8-linkedin.svg"
                    alt=""
                    className="h-4 w-4 object-contain"
                    width={16}
                    height={16}
                    draggable={false}
                  />
                )}
                {lastAuthMethod === 'apple' && (
                  <svg
                    viewBox="0 0 50 50"
                    width={16}
                    height={16}
                    className="h-4 w-4 object-contain"
                    aria-hidden="true"
                  >
                    <path fill="#808080" d="M 44.527344 34.75 C 43.449219 37.144531 42.929688 38.214844 41.542969 40.328125 C 39.601563 43.28125 36.863281 46.96875 33.480469 46.992188 C 30.46875 47.019531 29.691406 45.027344 25.601563 45.0625 C 21.515625 45.082031 20.664063 47.03125 17.648438 47 C 14.261719 46.96875 11.671875 43.648438 9.730469 40.699219 C 4.300781 32.429688 3.726563 22.734375 7.082031 17.578125 C 9.457031 13.921875 13.210938 11.773438 16.738281 11.773438 C 20.332031 11.773438 22.589844 13.746094 25.558594 13.746094 C 28.441406 13.746094 30.195313 11.769531 34.351563 11.769531 C 37.492188 11.769531 40.8125 13.480469 43.1875 16.433594 C 35.421875 20.691406 36.683594 31.78125 44.527344 34.75 Z M 31.195313 8.46875 C 32.707031 6.527344 33.855469 3.789063 33.4375 1 C 30.972656 1.167969 28.089844 2.742188 26.40625 4.78125 C 24.878906 6.640625 23.613281 9.398438 24.105469 12.066406 C 26.796875 12.152344 29.582031 10.546875 31.195313 8.46875 Z"/>
                  </svg>
                )}
              </span>
            </div>
          )}
          {authMode !== 'forgot' && (
            <SocialProvidersRow
              onGoogleClick={handleGoogleSignIn}
              onMicrosoftClick={handleMicrosoftSignIn}
              onLinkedInClick={handleLinkedInSignIn}
              lastAuthMethod={lastAuthMethod}
              showApplePlaceholder={showApplePlaceholder}
            />
          )}
          {/* Desktop provider buttons removed: using icon row across all breakpoints */}

          {authMode !== 'forgot' && (
            <div className="relative mt-20 mb-10">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">OR CONTINUE WITH EMAIL</span>
              </div>
            </div>
          )}

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(async (values) => {
                // Common email validation for all modes
                const basic = validateEmailFormat(values.email);
                if (!basic.isValid) {
                  form.setError('email', { type: 'manual', message: basic.error || 'Please enter a valid email address' });
                  return;
                }
                const sugg = validateEmailWithSuggestion(values.email);
                if (sugg.suggestion && sugg.suggestion !== values.email) {
                  form.setError('email', { type: 'manual', message: `Did you mean ${sugg.suggestion}?` });
                  return;
                }
                if (authMode === 'signup') {
                  if (honeypotRef.current && honeypotRef.current.value) {
                    return;
                  }
                  if (signupNameFieldsEnabled) {
                    if (!values.first_name?.trim()) {
                      form.setError('first_name', { type: 'manual', message: 'First name is required' });
                      return;
                    }
                    if (!values.last_name?.trim()) {
                      form.setError('last_name', { type: 'manual', message: 'Last name is required' });
                      return;
                    }
                  }
                  if (deepValidationEnabled) {
                    try {
                      const deep = await Promise.race([
                        validateEmailDeep(values.email),
                        new Promise((resolve) => setTimeout(() => resolve({ timeout: true } as any), 1800)),
                      ]);
                      if ((deep as any)?.timeout) {
                        // allow silently
                      } else {
                        const r = deep as any;
                        if (r && (r.isValid === false || r.deliverable === false)) {
                          const suggested = r.suggestion ? ` Did you mean ${r.suggestion}?` : '';
                          form.setError('email', { type: 'manual', message: r.error || (`This email address appears to be invalid.${suggested}`) });
                          return;
                        }
                        if (r.disposable) {
                          form.setError('email', { type: 'manual', message: 'Disposable email addresses are not supported. Use a personal email.' });
                          return;
                        }
                        if (typeof r.error === 'string' && r.error.toLowerCase().includes('role-based')) {
                          form.setError('email', { type: 'manual', message: 'Role-based email addresses (info@, admin@) are not supported. Use a personal email.' });
                          return;
                        }
                      }
                    } catch {
                    }
                  }
                }
                await onSubmit(values);
              })}
              className="space-y-4"
            >
              {authMode === 'signup' && signupNameFieldsEnabled && (
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="enter your first name"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            className="placeholder:text-slate-300/60"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="enter your last name"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            className="placeholder:text-slate-300/60"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input 
                          placeholder={authMode === 'forgot' ? "Your email address" : "enter your email"}
                          autoComplete="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck={false}
                          className={"placeholder:text-slate-300/60"}
                          {...field}
                          onBlur={(e) => {
                            field.onBlur();
                          }}
                        />
                        {authMode === 'signin' && lastAuthMethod === 'email' && (
                          <LastUsedIndicator />
                        )}
                      </div>
                    </FormControl>
                    {/* inline suggestion removed to avoid duplicate with error message */}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {(authMode === 'signin' || authMode === 'signup') && (
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder={"enter your password"}
                          autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                          className={"placeholder:text-slate-300/60"}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <input ref={honeypotRef} name="company_website" className="hidden" autoComplete="off" tabIndex={-1} aria-hidden="true" />

              {authMode === 'signup' && (
                <div className="text-center text-xs text-muted-foreground mb-3">
                  By clicking, you agree to our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a> and <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>.
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-[#222222] hover:bg-[#333333]" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="mr-2" ariaLabel="Processing" />
                    {authMode === 'forgot' ? "Sending..." : 
                     authMode === 'signup' ? "Creating account..." : 
                     "Signing in..."}
                  </>
                ) : (
                  authMode === 'forgot' ? "Send Reset Instructions" :
                  authMode === 'signup' ? "Get Started →" :
                  "Sign in"
                )}
              </Button>
            </form>
          </Form>

          <div className="flex flex-col gap-2 text-center text-sm">
            {authMode === 'signin' && (
              <>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setAuthMode('forgot')}
                >
                  Forgot your password?
                </button>
                <button
                  type="button"
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setAuthMode('signup')}
                >
                  Don't have an account? <span className="text-blue-600 underline">Sign up</span>
                </button>
              </>
            )}
            {authMode === 'signup' && (
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setAuthMode('signin')}
              >
                Already have an account? <span className="text-blue-600 underline">Sign in</span>
              </button>
            )}
            {authMode === 'forgot' && (
              <button
                type="button"
                className="text-blue-600 underline hover:no-underline"
                onClick={() => setAuthMode('signin')}
              >
                Back to sign in
              </button>
            )}
          </div>
        </CardContent>
        </Card>
        {authMode === 'signup' && (
          <div className="lg:hidden mt-14 mb-20 text-white">
            <p className="text-xs text-white/80 mb-3">World-class market data</p>
            <div className="flex items-center gap-8 opacity-95">
              <div className="h-6 flex items-center">
                <img src="/logos/partners/bloomberg-white.svg" alt="Bloomberg" className="h-6 w-auto object-contain translate-y-[5px]" loading="lazy" />
              </div>
              <div className="h-6 flex items-center">
                <img src="/logos/partners/fundserv.svg" alt="Fundserv" className="h-6 w-auto object-contain invert" loading="lazy" />
              </div>
              <div className="h-6 flex items-center">
                <img src="/logos/partners/morningstar-white.svg" alt="Morningstar" className="h-6 w-auto object-contain" loading="lazy" />
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Waitlist Dialog */}
      <WaitlistDialog 
        open={waitlist?.showDialog || false} 
        onOpenChange={(open) => {
          if (!open) clearWaitlistDialog();
        }}
        userEmail={waitlist?.email || ''}
      />
    </div>
  );
}
